import { registerClearCacheListener } from "../../Caching";
import { hasFlag, hasGlobalFlag, setFlag, setGlobalFlag } from "../../Flags";
import { DataStore } from "../../generic/DataStore";
import { PlayerContextState } from "../../PlayerContext";
import { registerRetirementListener } from "../../Retire";
import {
  getMaxOfMaxPrimarySchoolLevels,
  SpellElement,
} from "../../spells/ElementsAndSchools";
import { Dungeon } from "./Dungeon";
import { DungeonFloor } from "./DungeonFloor";

const Dungeons = new DataStore<Dungeon>();
const DungeonFloors = new DataStore<DungeonFloor>();

export { Dungeons, DungeonFloors };

export function getUnlockedDungeons(state: PlayerContextState): Dungeon[] {
  return Dungeons.getAll().filter((dungeon) => dungeon.isUnlocked(state));
}

export function isDungeonFloorCompletedThisRun(
  state: PlayerContextState,
  floor: DungeonFloor | string,
): boolean {
  return hasFlag(
    state,
    (typeof floor === "string" ? floor : floor.getId()) +
      "_dungeon_floor_completed",
  );
}

export function isDungeonFloorEverCompleted(
  state: PlayerContextState,
  floor: DungeonFloor | string,
): boolean {
  return hasGlobalFlag(
    state,
    (typeof floor === "string" ? floor : floor.getId()) +
      "_dungeon_floor_completed",
  );
}

export function setDungeonFloorCompleted(
  state: PlayerContextState,
  floorId: string,
): PlayerContextState {
  state = setFlag(floorId + "_dungeon_floor_completed")(state);
  state.thisRun.lastDungeonFloorCompleted = floorId;
  return setGlobalFlag(floorId + "_dungeon_floor_completed")(state);
}

export function isStuckOnBoss(state: PlayerContextState): boolean {
  return (
    state.global.lastDungeonFloorCompletedLastRun ==
      state.thisRun.lastDungeonFloorCompleted &&
    state.thisRun.lastDungeonFloorCompleted != null &&
    state.thisRun.endingTriggered == null &&
    !(
      state.primaryElement === SpellElement.Darkness &&
      state.thisRun.lastDungeonFloorCompleted != "darknessTemple3F"
    )
  );
}

export function minMPLRecommendation(state: PlayerContextState): number {
  const unlockedDungeons = getUnlockedDungeons(state);
  const floors = unlockedDungeons
    .map((dungeon) =>
      dungeon
        .getDungeonFloors()
        .filter(
          (floor) => floor.isUnlocked(state) && floor.isMainStoryDungeonFloor(),
        ),
    )
    .flat();
  const maxLowRecommendation = Math.max(
    ...floors.map((floor) => floor.getMinimumMPLRecommendation() ?? 0),
  );
  return maxLowRecommendation;
}

export function mplIsTooLow(state: PlayerContextState): boolean {
  const maxLowRecommendation = minMPLRecommendation(state);
  const currentMPL = getMaxOfMaxPrimarySchoolLevels(state);
  return currentMPL < maxLowRecommendation;
}

export function maxMPLRecommendation(state: PlayerContextState): number {
  const unlockedDungeons = getUnlockedDungeons(state);
  const floors = unlockedDungeons
    .map((dungeon) =>
      dungeon
        .getDungeonFloors()
        .filter(
          (floor) => floor.isUnlocked(state) && floor.isMainStoryDungeonFloor(),
        ),
    )
    .flat();
  const maxHighRecommendation = Math.max(
    ...floors.map((floor) => floor.getMaximumMPLRecommendation() ?? Infinity),
  );
  return maxHighRecommendation;
}

export function mplIsTooHigh(state: PlayerContextState): boolean {
  const minHighRecommendation = maxMPLRecommendation(state);
  const currentMPL = getMaxOfMaxPrimarySchoolLevels(state);
  return currentMPL > minHighRecommendation;
}

registerRetirementListener("lastDungeonFloorCompleted", (state, isMock) => {
  if (!isMock) {
    state.global.lastDungeonFloorCompletedLastRun =
      state.thisRun.lastDungeonFloorCompleted;
  }
  return state;
});

registerClearCacheListener("FloorCacheClearer", () => {
  DungeonFloors.getAll().forEach((floor) => floor.invalidateFightBossCache());
});
